import React, { ComponentProps, ForwardedRef, forwardRef } from 'react';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import { ButtonGroup } from '../../ButtonGroup';
import { Typography } from '../../Typography';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/bulk-actions-bar/bulk-actions-bar.module.css';
import { tokens } from '@danske/sapphire-css/themes/index';
import { filterDOMProps } from 'react-aria/filterDOMProps';

export interface BulkActionsBarProps
  extends GlobalDomAttributes,
    SapphireStyleProps {
  /**
   * Title displayed on the left side of the bar.
   * Typically used to show number of selected items.
   */
  title: React.ReactNode;
  /**
   * Action buttons rendered inside a {@link ButtonGroup} on the right side of the bar.
   */
  actions: ComponentProps<typeof ButtonGroup>['children'];
}

/**
 * Helper component to be used together with {@link InteractiveTable}
 * or {@link InteractiveList} to implement the layout and appearance of
 * Sapphire UI for bulk actions.
 */
export const BulkActionsBar = forwardRef(function BulkActionsBar(
  props: BulkActionsBarProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { title, actions, ...rest } = props;
  const { styleProps } = useSapphireStyleProps(props);
  return (
    <div
      style={{ ...styleProps.style }}
      {...filterDOMProps(rest, { global: true })}
      className={clsx(
        styles['sapphire-bulk-actions-bar'],
        styleProps.className
      )}
      ref={ref}
    >
      <Typography.Body size="md" fontWeight={tokens.font.weightDefaultMedium}>
        {title}
      </Typography.Body>
      <ButtonGroup>{actions}</ButtonGroup>
    </div>
  );
});
